/**
 * Avatar joueur : forme geometrique deterministe, seedee par playerId, dans la
 * couleur du joueur. Deux joueurs ont toujours des avatars distincts tant que
 * leurs couleurs different, et le meme joueur garde le sien d'un ecran a l'autre.
 */
import { useMemo, type ReactElement } from 'react';
import { PLAYER_COLORS } from '@retusch/shared';
import { avatarById, avatarSrc } from '../account/avatars.js';

export interface AvatarProps {
  readonly playerId: string;
  readonly colorIndex: number;
  readonly size?: number;
  /** Grise l'avatar d'un joueur deconnecte. */
  readonly dimmed?: boolean;
  /** Coche verte : dessin rendu, devinette jouee. */
  readonly done?: boolean;
  /**
   * Identifiant d'avatar (cf. account/avatars). Quand il est la, il remplace la
   * forme geometrique. Un avatar payant se voit a son cercle DORE, contre un
   * cercle blanc pour les autres.
   *
   * Le cercle, et rien d'autre : la pastille cadeau qui s'y ajoutait posait un
   * second signe sur un objet de 28 px dans le rail des scores, ou elle
   * mangeait l'image sans rien apprendre que la couleur du cercle ne disait
   * deja.
   */
  readonly avatar?: string | null;
}

/** Hash stable : meme id, meme forme, sur tous les clients. */
function hash(value: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function shapePath(seed: number): string {
  const kind = seed % 6;
  switch (kind) {
    case 0:
      return 'M50 8 L92 50 L50 92 L8 50 Z';
    case 1:
      return 'M50 10 L88 32 L88 68 L50 90 L12 68 L12 32 Z';
    case 2:
      return 'M50 12 L90 84 L10 84 Z';
    case 3:
      return 'M20 20 H80 V80 H20 Z';
    case 4:
      return 'M50 10 A40 40 0 1 1 49.9 10 Z';
    default:
      return 'M50 10 L74 34 L90 50 L66 74 L50 90 L26 66 L10 50 L34 26 Z';
  }
}

export function Avatar({
  playerId,
  colorIndex,
  size = 48,
  dimmed = false,
  done = false,
  avatar = null,
}: AvatarProps): ReactElement {
  const choice = avatarById(avatar);
  const seed = useMemo(() => hash(playerId), [playerId]);
  const color = PLAYER_COLORS[colorIndex % PLAYER_COLORS.length] ?? PLAYER_COLORS[0];
  const rotation = seed % 4 === 0 ? 0 : (seed % 45) - 22;

  return (
    <span
      className="avatar"
      data-tier={choice?.tier ?? 'shape'}
      style={{ width: size, height: size, opacity: dimmed ? 0.35 : 1 }}
    >
      {choice !== null ? (
        <img
          className="avatar__image"
          src={avatarSrc(choice.id, size)}
          alt=""
          width={size}
          height={size}
          loading="lazy"
        />
      ) : (
      <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
        <path
          d={shapePath(seed)}
          fill={color}
          transform={`rotate(${rotation} 50 50)`}
        />
        {/* Une encoche claire desambigue deux formes proches. */}
        <circle cx={32 + (seed % 12)} cy={34 + ((seed >> 3) % 10)} r="7" fill="#ffffff30" />
      </svg>
      )}
      {done && (
        <span className="avatar__check" aria-hidden="true">
          ✓
        </span>
      )}
    </span>
  );
}

export function AvatarSlot({ size = 48 }: { size?: number }): ReactElement {
  return <span className="avatar avatar--empty" style={{ width: size, height: size }} />;
}
